import React from 'react';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import AdditionalInformation from 'components/AdditionalInformation/AdditionalInformation';
import { DataType } from 'store/ducks/DataEntry/Branch/types';
import CustomSelect from 'components/CustomSelect';
import { DatePicker, Button } from 'antd';
import { IEntityMaintainingRegistry } from 'store/ducks/DataEntry/Entities/types';
import dayjs from 'dayjs';
import * as Styled from '../Tables/DataEntry/DataEntry.styled';

export const getEntityMaintainingColumns = (
  handleSave: (record: IEntityMaintainingRegistry) => void,
  handleRemoveRow: (key: number) => void,
  handleInactivate: (record: IEntityMaintainingRegistry) => void,
  handleAddRow: (record: { key: React.Key }, index: number) => void,
  countries: { value: string; label: string }[],
  entityTypes: { value: string; label: string }[],
  competentAuthorities: { value: string; label: string }[],
  t: any,
): EditableColumnType[] => [
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ENTITY_MAINTAINING.LEI_FINANCIAL_ENTITY_MAINTAINING')}</p>
          <p>B_01.01.0010</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t(
            'ENTITY_MAINTAINING.LEI_FINANCIAL_ENTITY_MAINTAINING_INFO',
          )}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'lei_entity_maintaining_register_information',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ENTITY_MAINTAINING.NAME_OF_FINANCIAL_ENTITY_MAINTAINING')}</p>
          <p>B_01.01.0020</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t(
            'ENTITY_MAINTAINING.NAME_OF_FINANCIAL_ENTITY_MAINTAINING_INFO',
          )}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'financial_entity_name',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ENTITY_MAINTAINING.COUNTRY_OF_FINANCIAL_ENTITY')}</p>
          <p>B_01.01.0030</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('ENTITY_MAINTAINING.COUNTRY_OF_FINANCIAL_ENTITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'country_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={countries}
        value={record.country_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            country_id: value,
          } as IEntityMaintainingRegistry)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ENTITY_MAINTAINING.TYPE_OF_FINANCIAL_ENTITY')}</p>
          <p>B_01.01.0040</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('ENTITY_MAINTAINING.TYPE_OF_FINANCIAL_ENTITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'entity_type_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => {
      return (
        <CustomSelect
          options={entityTypes}
          value={record.entity_type_id}
          onChange={(value: any) => {
            handleSave({
              ...record,
              entity_type_id: value,
            } as IEntityMaintainingRegistry);
          }}
          filterOption={(input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ENTITY_MAINTAINING.COMPETENT_AUTHORITY')}</p>
          <p>B_01.01.0050</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('ENTITY_MAINTAINING.COMPETENT_AUTHORITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'competent_authority_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={competentAuthorities}
        value={record.competent_authority_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            competent_authority_id: value,
          } as IEntityMaintainingRegistry)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('ENTITY_MAINTAINING.DATE_OF_THE_REPORTING')}</p>
          <p>B_01.01.0060</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('ENTITY_MAINTAINING.DATE_OF_THE_REPORTING_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'reporting_date',
    editable: false,
    className: 'highlighted-column',
    render: (text: string, record: DataType) => {
      const entityRecord = record as IEntityMaintainingRegistry;
      return (
        <DatePicker
          value={text ? dayjs(text) : null}
          format="YYYY-MM-DD"
          onClick={(e) => e.stopPropagation()}
          onChange={(date) => {
            const formattedDateString = date
              ? dayjs(date).format('YYYY-MM-DD')
              : '';
            const updatedRow: IEntityMaintainingRegistry = {
              ...entityRecord,
              reporting_date: formattedDateString,
            };
            handleSave(updatedRow);
          }}
        />
      );
    },
  },
  {
    title: t('GENERAL.ACTIONS'),
    key: 'actions',
    render: (_: any, record) => (
      <>
        {record.isNew ? (
          <Button danger onClick={() => handleRemoveRow(record.key)}>
            {t('GENERAL.REMOVE')}
          </Button>
        ) : (
          <Button
            danger
            onClick={() =>
              handleInactivate(record as IEntityMaintainingRegistry)
            }
          >
            {t('GENERAL.SOFT_DELETE')}
          </Button>
        )}
      </>
    ),
  },
];
